import React, { useMemo, useState } from 'react';
import { cn, useToast } from '@crisellevaldez/speculo-ui';
import {
  Flame,
  MessageCircle,
  PauseCircle,
  PlayCircle,
  User,
  RotateCw,
  RotateCcw,
} from 'lucide-react';
import type { Contact } from '../../../../types/contact.types';
import { RemiCardHeader } from './RemiCardHeader';
import { useOrganizationStore } from '../../../../stores/useOrganizationStore';
import { formatDate } from '../../../../utils/dateTime';
import { formatPauseReason, unpauseContact } from '../../../../api/contacts';
import { PauseContactDialog } from './PauseContactDialog';

type StatusOption = 'handoff' | 'nurturing' | 'paused';

interface StatusDef {
  id: StatusOption;
  label: string;
  icon: React.ReactNode;
  pickedBg: string;
  pickedBorder: string;
  pickedText: string;
  pickedShadow: string;
  dotBg: string;
  desc: string;
}

const STATUS_OPTIONS: StatusDef[] = [
  {
    id: 'handoff',
    label: 'Handoff',
    icon: (
      <Flame
        className="w-4 h-4 lg:w-4 lg:h-4 2xl:w-5 2xl:h-5"
        strokeWidth={2.25}
        style={{ stroke: 'url(#statusGradHandoff)' }}
      />
    ),
    pickedBg:
      'bg-gradient-to-br from-red-50 via-white to-red-50/40 dark:from-red-500/10 dark:via-card dark:to-red-500/5',
    pickedBorder: 'border-red-500/50 dark:border-red-400/40',
    pickedText: 'text-red-700 dark:text-red-300',
    pickedShadow:
      'shadow-[0_0_12px_rgba(220,38,38,0.15)] dark:shadow-[0_0_12px_rgba(248,113,113,0.1)]',
    dotBg: 'bg-red-600 dark:bg-red-400',
    desc: 'Remi is still actively nurturing, but thinks you should take over the conversation.',
  },
  {
    id: 'nurturing',
    label: 'Remi is nurturing',
    icon: (
      <MessageCircle
        className="w-4 h-4 lg:w-4 lg:h-4 2xl:w-5 2xl:h-5"
        strokeWidth={2.25}
        style={{ stroke: 'url(#statusGradNurturing)' }}
      />
    ),
    pickedBg:
      'bg-gradient-to-br from-amber-50 via-white to-amber-50/40 dark:from-amber-500/10 dark:via-card dark:to-amber-500/5',
    pickedBorder: 'border-amber-500/50 dark:border-amber-400/40',
    pickedText: 'text-amber-800 dark:text-amber-200',
    pickedShadow:
      'shadow-[0_0_12px_rgba(217,155,20,0.15)] dark:shadow-[0_0_12px_rgba(251,191,36,0.1)]',
    dotBg: 'bg-amber-700 dark:bg-amber-300',
    desc: 'Lead is not ready for agent interaction and Remi is still getting them there.',
  },
  {
    id: 'paused',
    label: 'Remi is paused',
    icon: (
      <PauseCircle
        className="w-4 h-4 lg:w-4 lg:h-4 2xl:w-5 2xl:h-5 text-foreground/60"
        strokeWidth={2.25}
      />
    ),
    pickedBg: 'bg-muted/50 dark:bg-foreground/[0.04]',
    pickedBorder: 'border-foreground/40 dark:border-foreground/30',
    pickedText: 'text-foreground',
    pickedShadow:
      'shadow-[0_0_12px_rgba(0,0,0,0.06)] dark:shadow-[0_0_12px_rgba(255,255,255,0.04)]',
    dotBg: 'bg-foreground/60',
    desc: 'Outreach is paused for this contact. Remi has stopped all channels.',
  },
];

interface SuggestedStatusCardProps {
  contact: Contact | null;
  assistantName?: string;
  /** Called when the agent clicks "Give back to {assistant}" on a claimed
   *  Handoff status. Opts the contact back into the flywheel. */
  onGiveBackToRemi?: () => void;
  /** Fires after a successful pause — gives the parent the raw reason (no
   *  prefix) so it can do an optimistic update on its local contact copy
   *  and refetch from GraphQL. */
  onPaused?: (reason: string) => void;
  /** Fires after a successful resume so the parent can optimistically clear
   *  the paused state on its local contact copy and refetch from GraphQL. */
  onResumed?: () => void;
}

/**
 * Read-only card with three tiles: Handoff, Nurturing, Remi-is-paused.
 *
 * Picked precedence (strongest signal first):
 *   1. AI paused on the contact → 'paused'
 *   2. HANDOFF                   → 'handoff'
 *   3. NURTURING                 → 'nurturing'
 *
 * APPOINTMENT_SET / DNC / UNKNOWN aren't surfaced — the card renders
 * nothing in those cases.
 */
export const SuggestedStatusCard: React.FC<SuggestedStatusCardProps> = ({
  contact,
  assistantName = 'Remi',
  onGiveBackToRemi,
  onPaused,
  onResumed,
}) => {
  const organization = useOrganizationStore((s) => s.organization);
  const { showToast } = useToast();
  const [pauseDialogOpen, setPauseDialogOpen] = useState(false);
  const [isResuming, setIsResuming] = useState(false);

  const handleResume = async (contactId: string) => {
    if (isResuming || !organization?.id) return;
    setIsResuming(true);
    const contactName =
      `${contact?.firstName ?? ''} ${contact?.lastName ?? ''}`.trim() || 'this contact';
    try {
      await unpauseContact(contactId, organization.id);
      showToast({
        message: `${assistantName} has resumed outreach for ${contactName}.`,
        variant: 'success',
      });
      onResumed?.();
    } catch (e) {
      showToast({
        message: e instanceof Error ? e.message : 'Failed to resume. Please try again.',
        variant: 'error',
      });
    } finally {
      setIsResuming(false);
    }
  };

  const picked: StatusOption | null = useMemo(() => {
    if (!contact) return null;
    if (contact.aiPaused === true) return 'paused';
    switch (contact.suggestedStatus?.status) {
      case 'HANDOFF':
        return 'handoff';
      case 'NURTURING':
        return 'nurturing';
      default:
        return null;
    }
  }, [contact]);

  if (!contact || !picked) return null;

  const pickedDef = STATUS_OPTIONS.find((o) => o.id === picked)!;

  // Claimed Handoff reads differently from a fresh Handoff suggestion: the
  // contact is already being worked by an agent, so the framing flips from
  // "Remi is still nurturing, but you should take over" to "the contact
  // has been claimed, Remi has stepped back." Who claimed it lives in the
  // dedicated "Claimed by …" row below, so the description deliberately
  // doesn't repeat the name — it just sets the state and reminds the
  // agent they can give it back.
  // `claimedLead` falls back to the CRM-assigned agent when nothing was
  // claimed, so it is truthy for any owned contact and would read a pending
  // handoff as claimed. `hasClaimedLead` tracks the claim itself.
  const isClaimedHandoff = picked === 'handoff' && contact.hasClaimedLead === true;
  const handoffClaimedLabel = 'Claimed Handoff';
  const handoffClaimedDesc = `This contact has been claimed. ${assistantName} is no longer actively nurturing. You can give it back any time.`;

  return (
    <div
      className="
        relative overflow-hidden rounded-2xl
        px-4 py-3 sm:px-5 sm:py-3.5 lg:px-6 lg:py-3.5 xl:px-7 xl:py-5
        bg-white dark:bg-card
        ring-1 ring-foreground/10
        shadow-md
      "
    >
      <svg width="0" height="0" className="absolute" aria-hidden>
        <defs>
          <linearGradient id="statusGradHandoff" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#fb923c" />
            <stop offset="100%" stopColor="#ef4444" />
          </linearGradient>
          <linearGradient id="statusGradNurturing" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#facc15" />
            <stop offset="100%" stopColor="#f59e0b" />
          </linearGradient>
        </defs>
      </svg>

      <div className="relative mb-3 lg:mb-3">
        <RemiCardHeader
          eyebrow="Suggested Contact Status"
          title={
            <>
              {assistantName} recommends:{' '}
              <strong className={cn('font-semibold', pickedDef.pickedText)}>
                {isClaimedHandoff ? handoffClaimedLabel : pickedDef.label}
              </strong>
            </>
          }
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 lg:gap-3">
        {STATUS_OPTIONS.map((o) => {
          const isPicked = picked === o.id;
          // Only the Paused tile is interactive — clicking it on an
          // unpaused contact opens the confirmation dialog. The other tiles
          // remain read-only suggestions.
          const isPauseAction = o.id === 'paused' && !isPicked && Boolean(contact.id);
          const handleTileClick = isPauseAction ? () => setPauseDialogOpen(true) : undefined;
          return (
            <div
              key={o.id}
              role={isPauseAction ? 'button' : undefined}
              tabIndex={isPauseAction ? 0 : undefined}
              onClick={handleTileClick}
              onKeyDown={
                isPauseAction
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setPauseDialogOpen(true);
                      }
                    }
                  : undefined
              }
              className={cn(
                'relative text-left rounded-lg border p-2.5 lg:p-3',
                isPicked
                  ? `${o.pickedBg} ${o.pickedBorder} ${o.pickedShadow}`
                  : 'border-foreground/15 bg-card',
                isPauseAction &&
                  'cursor-pointer hover:border-foreground/40 hover:bg-muted/40 transition-colors'
              )}
            >
              <div className="flex items-center gap-2 mb-2 lg:mb-2.5">
                <span className="flex items-center justify-center flex-shrink-0">{o.icon}</span>
                <span
                  className={cn(
                    'text-xs lg:text-xs 2xl:text-sm font-semibold flex-1 truncate',
                    isPicked ? o.pickedText : 'text-foreground'
                  )}
                >
                  {o.label}
                </span>
                <span
                  className={cn(
                    'w-3.5 h-3.5 lg:w-4 lg:h-4 rounded-full border flex items-center justify-center flex-shrink-0',
                    isPicked ? o.pickedBorder : 'border-foreground/30'
                  )}
                >
                  {isPicked && (
                    <span className={cn('w-1.5 h-1.5 lg:w-2 lg:h-2 rounded-full', o.dotBg)} />
                  )}
                </span>
              </div>
              {/* Default tile description — shown for every tile in the
                  unpicked state. When Paused is picked with a recorded
                  reason, the description is replaced by the "Reason: …" line
                  below so the reason gets prominence; with no recorded
                  reason the default blurb stays so the tile is never blank.
                  Claimed Handoff swaps in a different blurb
                  that reflects "agent has taken over, Remi stopped
                  nurturing" instead of the default "Remi still nurturing,
                  hand off to you" copy. */}
              {(o.id !== 'paused' || !isPicked || !contact.aiPausedReason) && (
                <p className="text-[11px] lg:text-[11px] xl:text-xs 2xl:text-[13px] leading-snug text-foreground/80">
                  {o.id === 'handoff' && isPicked && isClaimedHandoff
                    ? handoffClaimedDesc
                    : o.desc}
                </p>
              )}
              {o.id === 'paused' && isPicked && contact.aiPausedReason && (
                <p className="text-[11px] lg:text-[11px] xl:text-xs 2xl:text-[13px] leading-snug text-foreground/80">
                  <span className="font-semibold">Reason:</span>{' '}
                  {formatPauseReason(contact.aiPausedReason)}
                </p>
              )}

              {/* Nurturing-picked: flywheel enrollment detail. */}
              {o.id === 'nurturing' && isPicked && contact.flywheelOptOut === false && (
                <div className="mt-2.5 flex items-center flex-wrap gap-x-2 gap-y-1 text-[11px] lg:text-[11px] 2xl:text-xs text-amber-700 dark:text-amber-300">
                  <span className="flex items-center gap-1.5 min-w-0">
                    <RotateCw className="h-3 w-3 lg:h-3.5 lg:w-3.5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                    <span className="font-medium truncate">Enrolled in Follow-up Flywheel</span>
                  </span>
                </div>
              )}

              {/* Handoff-picked: claimed-by row + give-back action. */}
              {o.id === 'handoff' && isPicked && isClaimedHandoff && contact.claimedLead && (
                <div className="mt-2.5 flex items-center flex-wrap gap-2 text-[11px] lg:text-[11px] 2xl:text-xs text-amber-700 dark:text-amber-300">
                  <span className="flex items-center gap-1.5 min-w-0">
                    <User className="h-3 w-3 lg:h-3.5 lg:w-3.5 text-amber-600 dark:text-amber-400 flex-shrink-0" />
                    <span className="font-medium truncate">
                      Claimed by {contact.claimedLead.name}
                    </span>
                  </span>
                  {onGiveBackToRemi && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        onGiveBackToRemi();
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          e.stopPropagation();
                          onGiveBackToRemi();
                        }
                      }}
                      className="inline-flex items-center gap-1 text-[10px] lg:text-[10px] 2xl:text-[11px] font-medium px-1.5 py-0.5 rounded border border-foreground/20 bg-muted/60 text-foreground/80 hover:bg-muted hover:text-foreground hover:border-foreground/40 dark:bg-foreground/10 dark:hover:bg-foreground/15 cursor-pointer transition-colors whitespace-nowrap"
                    >
                      <RotateCcw className="h-2.5 w-2.5 lg:h-3 lg:w-3" strokeWidth={2.25} />
                      Give back to {assistantName}
                    </span>
                  )}
                </div>
              )}

              {/* Paused-picked: since date + resume action — below the reason. */}
              {o.id === 'paused' && isPicked && (
                <div className="mt-2 flex items-center flex-wrap gap-2 text-[10.5px] lg:text-[10.5px] 2xl:text-[11px] text-muted-foreground">
                  {contact.aiPausedAt && (
                    <span>Paused {formatDate(contact.aiPausedAt, organization?.timezone)}</span>
                  )}
                  {onResumed && contact.id && (
                    <span
                      role="button"
                      tabIndex={0}
                      aria-disabled={isResuming}
                      onClick={(e) => {
                        e.stopPropagation();
                        void handleResume(contact.id);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          e.stopPropagation();
                          void handleResume(contact.id);
                        }
                      }}
                      className="inline-flex items-center gap-1 text-[10px] lg:text-[10px] 2xl:text-[11px] font-medium px-1.5 py-0.5 rounded border border-amber-300/70 bg-amber-50 text-amber-700 hover:bg-amber-100 hover:border-amber-400 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300 dark:hover:bg-amber-500/20 cursor-pointer transition-colors whitespace-nowrap aria-disabled:opacity-60 aria-disabled:cursor-default"
                    >
                      <PlayCircle className="h-2.5 w-2.5 lg:h-3 lg:w-3" strokeWidth={2.25} />
                      {isResuming ? 'Resuming…' : `Resume ${assistantName}`}
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {organization?.id && contact.id && (
        <PauseContactDialog
          isOpen={pauseDialogOpen}
          onClose={() => setPauseDialogOpen(false)}
          contactId={contact.id}
          organizationId={organization.id}
          contactName={
            `${contact.firstName ?? ''} ${contact.lastName ?? ''}`.trim() || 'this contact'
          }
          assistantName={assistantName}
          onPaused={(rawReason) => {
            onPaused?.(rawReason);
          }}
        />
      )}
    </div>
  );
};
